import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, ShieldCheck, HeartHandshake, Sparkles, BrainCircuit, Brain, Quote, Star, Clock, MapPin, ChevronLeft, ChevronRight, Lock } from "lucide-react";
import { Button } from "@/components/ui/button";
import AppointmentDialog from "@/components/AppointmentDialog";
import { api } from "@/lib/api";
import { ssgData } from "@/lib/ssg";
import { useSiteSettings } from "@/components/GoogleReviews";
import { InstagramStrip } from "@/components/InstagramStrip";
import { DOCTORS_IMG_URL, LOGO_URL, DR_KAMLESH_IMG, DR_DHARMIN_IMG, INSTAGRAM_URL } from "@/lib/assets";
import useSEO from "@/hooks/useSEO";
import { useLang } from "@/context/LangContext";

const HERO_IMG = "https://images.unsplash.com/photo-1714976694810-85add1a29c96?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2MTJ8MHwxfHNlYXJjaHwxfHxlbXBhdGhldGljJTIwZG9jdG9yJTIwdGhlcmFwaXN0JTIwcGF0aWVudHxlbnwwfHx8fDE3Nzg3NDM1MDV8MA&ixlib=rb-4.1.0&q=85";
const ABOUT_IMG = "https://images.unsplash.com/photo-1771574204208-b47e2d863bc5?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzF8MHwxfHNlYXJjaHwyfHxjYWxtJTIwbW9kZXJuJTIwaG9zcGl0YWwlMjBpbnRlcmlvciUyMGNsaW5pY3xlbnwwfHx8fDE3Nzg3NDM1MDV8MA&ixlib=rb-4.1.0&q=85";
const NEURO_IMG = "https://images.unsplash.com/photo-1666214282459-c7dff167ecc0?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDJ8MHwxfHNlYXJjaHwyfHxtb2Rlcm4lMjBtZWRpY2FsJTIwaG9zcGl0YWwlMjB0ZWNobm9sb2d5JTIwY2xvc2UlMjB1cHxlbnwwfHx8fDE3Nzg3NDM1MzJ8MA&ixlib=rb-4.1.0&q=85";
const DEADD_IMG = "https://images.unsplash.com/photo-1777269749032-d8d458ae594d?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzF8MHwxfHNlYXJjaHwxfHxjYWxtJTIwbW9kZXJuJTIwaG9zcGl0YWwlMjBpbnRlcmlvciUyMGNsaW5pY3xlbnwwfHx8fDE3Nzg3NDM1MDV8MA&ixlib=rb-4.1.0&q=85";
const SEXOLOGY_IMG = "https://images.unsplash.com/photo-1604881991720-f91add269bed?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2MzR8MHwxfHNlYXJjaHw0fHxjb3VwbGUlMjBob2xkaW5nJTIwaGFuZHMlMjB3YXJtJTIwY2FsbSUyMGNvdW5zZWxsaW5nfGVufDB8fHx8MTc4ODE3OTAzNXww&ixlib=rb-4.1.0&q=85";
const DOC_IMG = "https://images.unsplash.com/photo-1631217868264-e5b90bb7e133?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2MTJ8MHwxfHNlYXJjaHwyfHxlbXBhdGhldGljJTIwZG9jdG9yJTIwdGhlcmFwaXN0JTIwcGF0aWVudHxlbnwwfHx8fDE3Nzg3NDM1MDV8MA&ixlib=rb-4.1.0&q=85";
const SITE = "https://manasneuropsychiatryhospital.in";

const services = [
  { icon: HeartHandshake, title: "Deaddiction Programme", desc: "Medically supervised detox, residential & outpatient pathways with relapse-prevention therapy.", to: "/deaddiction" },
  { icon: BrainCircuit, title: "Interventional Neuromodulation", desc: "rTMS for treatment-resistant depression, OCD and other neuropsychiatric conditions.", to: "/neuromodulation" },
  { icon: Sparkles, title: "Sexology & Sex Therapy", desc: "Discreet, evidence-based care for psychosexual problems, intimacy and relationship concerns.", to: "/sexology" },
  { icon: ShieldCheck, title: "General Psychiatry", desc: "Anxiety, depression, mood disorders, sleep, child & adolescent — comprehensive care under one roof.", to: "/general-psychiatry" },
  { icon: Brain, title: "Neuropsychiatry", desc: "Psychiatric symptoms caused by brain disease — epilepsy, stroke, head injury, Parkinson's, dementia and delirium.", to: "/neuropsychiatry" },
];

const stats = [
  { value: "35+", label: "Years of clinical practice" },
  { value: "1,00,000+", label: "Lives positively impacted" },
  { value: "100%", label: "Patient confidentiality" },
  { value: "24/7", label: "Emergency support line" },
];

export default function Home() {
  const settings = useSiteSettings();
  const jsonLd = useMemo(() => {
    const rating = Number(settings?.google_rating) || 0;
    const reviewCount = Number(settings?.google_review_count) || 0;
    const org = {
      "@type": ["MedicalOrganization", "Hospital"],
      "@id": `${SITE}/#organization`,
      name: "Manas Neuropsychiatry Hospital",
      alternateName: "Manas Hospital, Bhavnagar",
      description:
        "Neuropsychiatry hospital in Bhavnagar established in 1989 — general psychiatry, deaddiction, sexology and interventional neuromodulation (rTMS, mBPT, tDCS, ketamine IV infusion, naltrexone implants).",
      url: SITE,
      logo: LOGO_URL,
      image: DOCTORS_IMG_URL,
      telephone: "+91-94275-60101",
      email: "manasneuropsychiatryhospital@gmail.com",
      foundingDate: "1989",
      priceRange: "₹₹",
      currenciesAccepted: "INR",
      address: {
        "@type": "PostalAddress",
        streetAddress: "2nd floor, Carlton Square, Near Madhavdeep Complex, Kalanala",
        addressLocality: "Bhavnagar",
        postalCode: "364001",
        addressRegion: "Gujarat",
        addressCountry: "IN",
      },
      medicalSpecialty: ["Psychiatry", "Neuropsychiatry", "Addiction Medicine", "Sexology"],
      areaServed: ["Bhavnagar", "Sihor", "Palitana", "Talaja", "Mahuva", "Botad", "Amreli", "Saurashtra", "Gujarat"],
      availableService: [
        { "@type": "MedicalProcedure", name: "General psychiatry OPD consultation" },
        { "@type": "MedicalProcedure", name: "Deaddiction and detoxification programme" },
        { "@type": "MedicalProcedure", name: "Sex therapy and psychosexual medicine" },
        { "@type": "MedicalProcedure", name: "Repetitive transcranial magnetic stimulation (rTMS)" },
        { "@type": "MedicalProcedure", name: "Modified brief pulse therapy (mBPT)" },
        { "@type": "MedicalProcedure", name: "Transcranial direct current stimulation (tDCS)" },
        { "@type": "MedicalProcedure", name: "Ketamine intravenous infusion therapy" },
        { "@type": "MedicalProcedure", name: "Naltrexone drug implant" },
      ],
      openingHoursSpecification: [
        {
          "@type": "OpeningHoursSpecification",
          dayOfWeek: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"],
          opens: "09:00",
          closes: "20:00",
        },
      ],
      founder: { "@id": `${SITE}/doctors/dr-kamlesh-shah#physician` },
      employee: [
        { "@id": `${SITE}/doctors/dr-kamlesh-shah#physician` },
        { "@id": `${SITE}/doctors/dr-dharmin-shah#physician` },
      ],
    };
    if (settings?.google_review_url) org.sameAs = [settings.google_review_url];
    org.sameAs = [...(org.sameAs || []), INSTAGRAM_URL];
    if (rating > 0 && reviewCount > 0) {
      org.aggregateRating = {
        "@type": "AggregateRating",
        ratingValue: rating.toFixed(1),
        reviewCount,
        bestRating: "5",
        worstRating: "1",
      };
    }
    return {
      "@context": "https://schema.org",
      "@graph": [
        org,
        {
          "@type": "WebSite",
          "@id": `${SITE}/#website`,
          url: SITE,
          name: "Manas Neuropsychiatry Hospital",
          inLanguage: "en-IN",
          publisher: { "@id": `${SITE}/#organization` },
        },
        {
          "@type": "Physician",
          "@id": `${SITE}/doctors/dr-kamlesh-shah#physician`,
          name: "Dr. Kamlesh A. Shah",
          url: `${SITE}/doctors/dr-kamlesh-shah`,
          medicalSpecialty: ["Psychiatry", "Addiction Medicine", "Sexology"],
        },
        {
          "@type": "Physician",
          "@id": `${SITE}/doctors/dr-dharmin-shah#physician`,
          name: "Dr. Dharmin K. Shah",
          url: `${SITE}/doctors/dr-dharmin-shah`,
          medicalSpecialty: ["Psychiatry", "Addiction Medicine", "Sexology"],
        },
      ],
    };
  }, [settings]);

  useSEO({
    title: "Neuropsychiatrist Bhavnagar · Deaddiction & rTMS",
    description: "Manas Neuropsychiatry Hospital, Bhavnagar — psychiatry, deaddiction, sex therapy and interventional neuromodulation (rTMS, tDCS, Ketamine IV). Drs. Kamlesh & Dharmin Shah. Established 1989.",
    image: DOCTORS_IMG_URL,
    jsonLd,
  });
  const [bookOpen, setBookOpen] = useState(false);
  const { t } = useLang();
  const [testimonials, setTestimonials] = useState(() => ssgData("testimonials:general") || []);
  const [posts, setPosts] = useState(() => ssgData("blogPosts") || []);
  const [tIdx, setTIdx] = useState(0);
  const [tPaused, setTPaused] = useState(false);
  const [postOffset, setPostOffset] = useState(0);
  const [postsPaused, setPostsPaused] = useState(false);

  useEffect(() => {
    api.get("/testimonials?category=general").then(({ data }) => setTestimonials(data));
    api.get("/blog/posts").then(({ data }) => setPosts(data));
  }, []);

  useEffect(() => {
    if (tPaused || testimonials.length < 2) return;
    const id = setInterval(() => {
      setTIdx((i) => (i + 1) % testimonials.length);
    }, 7000);
    return () => clearInterval(id);
  }, [tPaused, testimonials.length]);

  useEffect(() => {
    if (postsPaused || posts.length <= 3) return;
    const id = setInterval(() => {
      setPostOffset((o) => (o + 3) % posts.length);
    }, 6500);
    return () => clearInterval(id);
  }, [postsPaused, posts.length]);

  const visiblePosts = posts.length
    ? Array.from({ length: Math.min(3, posts.length) }, (_, i) => posts[(postOffset + i) % posts.length])
    : [];
  const postGroupCount = posts.length > 3 ? Math.ceil(posts.length / 3) : 1;
  const currentGroup = Math.floor(postOffset / 3);

  useEffect(() => {
    const els = document.querySelectorAll(".reveal:not(.visible)");
    if (els.length === 0) return;
    const io = new IntersectionObserver(
      (entries) => entries.forEach((e) => e.isIntersecting && e.target.classList.add("visible")),
      { threshold: 0.12 }
    );
    els.forEach((el) => io.observe(el));
    return () => io.disconnect();
  }, [posts.length, testimonials.length]);

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-6 md:px-10 pt-12 md:pt-16 pb-16 md:pb-28">
          {/* Brand header — Logo + full hospital name */}
          <div className="text-center mb-16 md:mb-20 fade-up">
            <div className="flex justify-center mb-7">
              <img
                src={LOGO_URL}
                alt="Manas Neuropsychiatry Hospital logo"
                className="w-44 h-44 md:w-56 md:h-56 object-contain"
                style={{ mixBlendMode: "multiply" }}
                data-testid="hero-logo"
              />
            </div>
            <h1
              data-testid="hero-hospital-name"
              className="font-display text-3xl sm:text-4xl md:text-5xl text-[#2D2623] tracking-tight leading-[1.05] max-w-4xl mx-auto"
            >
              Manas Neuropsychiatry Hospital
            </h1>
            <p className="mt-3 text-sm md:text-base uppercase tracking-[0.22em] text-[#A39171]">
              {t("Deaddiction Centre · Sex Therapy Clinic")}
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-end">
            <div className="lg:col-span-7 fade-up">
              <p className="eyebrow text-[#A39171] mb-6">{t("Bhavnagar · Established 1989")}</p>
              <h2 className="font-display text-[44px] sm:text-6xl md:text-7xl leading-[0.95] tracking-tight text-[#2D2623]">
                {t("Where the mind")} <br />
                <span className="italic text-[#E07A5F]">{t("finds its way")}</span> {t("back home.")}
              </h2>
              <p className="mt-8 text-lg md:text-xl text-[#5C524E] max-w-xl leading-relaxed">
                {t("A premium neuropsychiatry, deaddiction and sex-therapy practice combining empathetic care with the latest in interventional neuromodulation.")}
              </p>

              <div className="mt-10 flex flex-col sm:flex-row gap-4">
                <Button
                  data-testid="hero-book-btn"
                  onClick={() => setBookOpen(true)}
                  className="rounded-full bg-[#2D2623] hover:bg-[#E07A5F] text-[#FDFBF7] px-8 h-14 text-base font-medium transition-all duration-300"
                >
                  {t("Book a Consultation")}
                  <ArrowUpRight className="ml-1 w-4 h-4" />
                </Button>
                <Link
                  to="/about"
                  data-testid="hero-about-link"
                  className="rounded-full border border-[#A39171]/60 hover:border-[#2D2623] text-[#2D2623] px-8 h-14 inline-flex items-center justify-center font-medium transition-all"
                >
                  {t("Meet the Doctors")}
                </Link>
              </div>

              <div className="mt-12 flex flex-wrap items-center gap-x-10 gap-y-4 text-sm text-[#5C524E]">
                <span className="flex items-center gap-2"><ShieldCheck className="w-4 h-4 text-[#E07A5F]" /> {t("Strict confidentiality")}</span>
                <span className="flex items-center gap-2"><HeartHandshake className="w-4 h-4 text-[#E07A5F]" /> {t("Family-centred care")}</span>
                <span className="flex items-center gap-2"><BrainCircuit className="w-4 h-4 text-[#E07A5F]" /> {t("Evidence-based protocols")}</span>
              </div>
            </div>

            <div className="lg:col-span-5 fade-up" style={{ animationDelay: "120ms" }}>
              <div className="relative rounded-[32px] overflow-hidden aspect-[4/5] shadow-[0_20px_80px_rgba(45,38,35,0.18)] bg-gradient-to-br from-[#F4EFE6] via-[#FDFBF7] to-[#E2C044]/20">
                <img
                  src={DOCTORS_IMG_URL}
                  alt="Dr. Kamlesh A. Shah and Dr. Dharmin K. Shah"
                  className="w-full h-full object-cover object-top"
                  loading="eager"
                  data-testid="hero-doctors-img"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#2D2623]/50 via-transparent to-transparent" />
                <div className="absolute bottom-6 left-6 right-6 bg-[#FDFBF7]/95 backdrop-blur-md rounded-2xl p-5 border border-white/40">
                  <p className="eyebrow text-[#A39171] mb-1.5">{t("Your Doctors")}</p>
                  <p className="text-sm text-[#2D2623] font-medium leading-tight">
                    Dr. Kamlesh A. Shah &amp; Dr. Dharmin K. Shah
                  </p>
                  <p className="text-xs text-[#5C524E] mt-1">Neuropsychiatry · Deaddiction · TMS (AIIMS)</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="bg-[#F4EFE6] border-y border-[#A39171]/15">
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-16 md:py-20">
          <div className="text-center mb-12 md:mb-14 max-w-3xl mx-auto reveal">
            <p className="eyebrow text-[#A39171] mb-4">{t("Trusted across generations")}</p>
            <h2 className="font-display text-3xl md:text-5xl text-[#2D2623] tracking-tight leading-tight">
              {t("A centre of excellence in")} <span className="italic text-[#E07A5F]">{t("mental health care")}</span>.
            </h2>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((s, i) => (
              <div key={i} className="reveal text-center" data-testid={`stat-${i}`}>
                <p className="font-display text-5xl md:text-6xl text-[#2D2623] tracking-tight">{s.value}</p>
                <p className="text-sm text-[#5C524E] mt-2 max-w-[180px] mx-auto">{t(s.label)}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SERVICES BENTO */}
      <section className="py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-14 reveal">
            <div className="max-w-2xl">
              <p className="eyebrow text-[#A39171] mb-4">{t("Our Services")}</p>
              <h2 className="font-display text-4xl md:text-5xl tracking-tight text-[#2D2623] leading-tight">
                {t("Four pillars of")} <span className="italic text-[#E07A5F]">{t("whole-person")}</span> {t("psychiatry.")}
              </h2>
            </div>
            <p className="text-[#5C524E] max-w-md text-lg">
              {t("Built around the patient — not the diagnosis. Every plan is personalised, multi-disciplinary and reviewed by our clinical board.")}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-12 gap-5">
            {services.map((s, i) => (
              <Link
                key={s.title}
                to={s.to}
                data-testid={`service-card-${i}`}
                className={`reveal group bg-[#F4EFE6] rounded-3xl p-8 md:p-10 border border-[#A39171]/10 hover:border-[#E07A5F]/40 hover:bg-[#FDFBF7] hover:shadow-[0_24px_60px_rgba(163,145,113,0.15)] transition-all duration-500 hover:-translate-y-1 ${
                  i === 0 ? "md:col-span-7" : i === 1 ? "md:col-span-5" : i === 2 ? "md:col-span-5" : "md:col-span-7"
                }`}
              >
                <div className="flex items-start justify-between mb-12">
                  <span className="w-14 h-14 rounded-2xl bg-[#2D2623] text-[#FDFBF7] flex items-center justify-center group-hover:bg-[#E07A5F] transition-colors">
                    <s.icon className="w-6 h-6" />
                  </span>
                  <ArrowUpRight className="w-6 h-6 text-[#A39171] group-hover:text-[#E07A5F] group-hover:rotate-45 transition-all duration-500" />
                </div>
                <h3 className="font-display text-3xl md:text-4xl text-[#2D2623] tracking-tight mb-4">{t(s.title)}</h3>
                <p className="text-[#5C524E] text-base md:text-lg leading-relaxed max-w-md">{t(s.desc)}</p>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* ABOUT PREVIEW */}
      <section className="bg-[#F4EFE6]/60 py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-10 grid grid-cols-1 md:grid-cols-12 gap-12 items-center">
          <div className="md:col-span-6 reveal">
            <div className="rounded-3xl overflow-hidden aspect-[5/6] relative">
              <img src={ABOUT_IMG} alt="Hospital interior" className="w-full h-full object-cover" />
            </div>
          </div>
          <div className="md:col-span-6 reveal">
            <p className="eyebrow text-[#A39171] mb-4">{t("About Manas")}</p>
            <h2 className="font-display text-4xl md:text-5xl text-[#2D2623] leading-tight tracking-tight">
              {t("A practice built around")} <span className="italic text-[#E07A5F]">{t("human dignity")}</span>.
            </h2>
            <p className="mt-6 text-lg text-[#5C524E] leading-relaxed">
              {t("Founded in Bhavnagar, Manas Neuropsychiatry Hospital brings together psychiatry, psychology, addiction medicine and modern neuromodulation under a single, calm roof. We believe that brilliant clinical work and quiet kindness are not opposites.")}
            </p>
            <ul className="mt-8 space-y-4">
              {[
                "Multi-disciplinary clinical board",
                "Confidential, judgment-free environment",
                "Integrated medication + therapy plans",
                "Family education throughout treatment",
              ].map((p) => (
                <li key={p} className="flex items-start gap-3 text-[#2D2623]">
                  <span className="w-1.5 h-1.5 rounded-full bg-[#E07A5F] mt-2.5" />
                  <span>{t(p)}</span>
                </li>
              ))}
            </ul>
            <Link
              to="/about"
              data-testid="about-cta"
              className="mt-10 inline-flex items-center gap-2 text-[#2D2623] hover:text-[#E07A5F] font-medium link-underline"
            >
              {t("Read our full story")} <ArrowUpRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* MEET YOUR DOCTORS */}
      <section className="py-24 md:py-32 bg-[#FDFBF7]">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-12 items-center">
            <div className="md:col-span-6 reveal order-2 md:order-1">
              <p className="eyebrow text-[#A39171] mb-4">{t("Meet Your Doctors")}</p>
              <h2 className="font-display text-4xl md:text-5xl text-[#2D2623] leading-tight tracking-tight">
                {t("Two generations,")} <span className="italic text-[#E07A5F]">{t("one practice")}</span>.
              </h2>
              <p className="mt-6 text-lg text-[#5C524E] leading-relaxed">
                <strong className="text-[#2D2623] font-medium">Dr. Kamlesh A. Shah</strong> {t("and")}
                <strong className="text-[#2D2623] font-medium"> Dr. Dharmin K. Shah</strong>{" "}
                {t("bring together decades of psychiatric expertise with cutting-edge interventional neuromodulation — under one calm, confidential roof.")}
              </p>
              <ul className="mt-7 space-y-3 text-[#2D2623]">
                <li className="flex items-start gap-3"><span className="w-1.5 h-1.5 rounded-full bg-[#E07A5F] mt-2.5" />{t("Neuropsychiatry · Deaddiction · Sex therapy")}</li>
                <li className="flex items-start gap-3"><span className="w-1.5 h-1.5 rounded-full bg-[#E07A5F] mt-2.5" />{t("TMS trained at AIIMS, New Delhi")}</li>
                <li className="flex items-start gap-3"><span className="w-1.5 h-1.5 rounded-full bg-[#E07A5F] mt-2.5" />{t("Life fellows / members, Indian Psychiatric Society")}</li>
                <li className="flex items-start gap-3"><span className="w-1.5 h-1.5 rounded-full bg-[#E07A5F] mt-2.5" />{t("Published in Annals of Indian Psychiatry & Journal of Psychosexual Health")}</li>
              </ul>
              <Link
                to="/about"
                data-testid="home-doctors-cta"
                className="mt-10 inline-flex items-center gap-2 px-7 h-12 rounded-full bg-[#2D2623] hover:bg-[#E07A5F] text-[#FDFBF7] transition-all font-medium"
              >
                {t("Read full profiles")} <ArrowUpRight className="w-4 h-4" />
              </Link>
            </div>
            <div className="md:col-span-6 reveal order-1 md:order-2">
              <div className="grid grid-cols-2 gap-4">
                <Link
                  to="/doctors/dr-kamlesh-shah"
                  data-testid="home-dr-kamlesh-card"
                  className="group bg-gradient-to-br from-[#F4EFE6] to-[#E2C044]/15 rounded-3xl overflow-hidden border border-[#A39171]/15 hover:shadow-[0_24px_60px_rgba(163,145,113,0.2)] transition-all duration-500 hover:-translate-y-1"
                >
                  <div className="aspect-[3/4] overflow-hidden bg-[#2D2623]/5">
                    <img
                      src={DR_KAMLESH_IMG}
                      alt="Dr. Kamlesh A. Shah"
                      className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-700"
                    />
                  </div>
                  <div className="p-5">
                    <p className="font-display text-xl text-[#2D2623] leading-tight">Dr. Kamlesh A. Shah</p>
                    <p className="text-xs text-[#5C524E] mt-1.5">MD Neuropsychiatry · FIPS · FIAPP</p>
                  </div>
                </Link>
                <Link
                  to="/doctors/dr-dharmin-shah"
                  data-testid="home-dr-dharmin-card"
                  className="group bg-gradient-to-br from-[#F4EFE6] to-[#E07A5F]/15 rounded-3xl overflow-hidden border border-[#A39171]/15 hover:shadow-[0_24px_60px_rgba(224,122,95,0.2)] transition-all duration-500 hover:-translate-y-1"
                >
                  <div className="aspect-[3/4] overflow-hidden bg-[#2D2623]/5">
                    <img
                      src={DR_DHARMIN_IMG}
                      alt="Dr. Dharmin K. Shah"
                      className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-700"
                    />
                  </div>
                  <div className="p-5">
                    <p className="font-display text-xl text-[#2D2623] leading-tight">Dr. Dharmin K. Shah</p>
                    <p className="text-xs text-[#5C524E] mt-1.5">MD · TMS Physician, AIIMS Delhi</p>
                  </div>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* TESTIMONIALS — single-card fade carousel */}
      <section className="py-24 md:py-32 bg-[#F4EFE6]/40">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-12 reveal">
            <div className="max-w-2xl">
              <p className="eyebrow text-[#A39171] mb-4">{t("In their own words")}</p>
              <h2 className="font-display text-4xl md:text-5xl text-[#2D2623] tracking-tight">
                {t("Stories that remind us")} <span className="italic text-[#E07A5F]">{t("why")}</span> {t("we do this.")}
              </h2>
            </div>
            {testimonials.length > 1 && (
              <div className="flex items-center gap-3" data-testid="testimonial-controls">
                <button
                  data-testid="testimonial-prev"
                  onClick={() => setTIdx((i) => (i - 1 + testimonials.length) % testimonials.length)}
                  aria-label="Previous testimonial"
                  className="w-12 h-12 rounded-full bg-[#FDFBF7] border border-[#A39171]/25 flex items-center justify-center text-[#2D2623] hover:bg-[#2D2623] hover:text-[#FDFBF7] hover:border-[#2D2623] transition-all"
                >
                  <ChevronLeft className="w-4 h-4" />
                </button>
                <button
                  data-testid="testimonial-next"
                  onClick={() => setTIdx((i) => (i + 1) % testimonials.length)}
                  aria-label="Next testimonial"
                  className="w-12 h-12 rounded-full bg-[#FDFBF7] border border-[#A39171]/25 flex items-center justify-center text-[#2D2623] hover:bg-[#2D2623] hover:text-[#FDFBF7] hover:border-[#2D2623] transition-all"
                >
                  <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            )}
          </div>

          {testimonials.length === 0 ? (
            <div className="text-center text-[#A39171] py-20">{t("Loading stories…")}</div>
          ) : (
            <div
              onMouseEnter={() => setTPaused(true)}
              onMouseLeave={() => setTPaused(false)}
              className="relative"
              data-testid="testimonial-carousel"
            >
              <article
                key={testimonials[tIdx].id}
                data-testid={`testimonial-${tIdx}`}
                className="fade-up bg-[#FDFBF7] border border-[#A39171]/15 rounded-3xl p-8 md:p-14 shadow-[0_20px_60px_rgba(163,145,113,0.12)] max-w-4xl mx-auto"
              >
                <div className="flex items-center justify-between mb-6">
                  <Quote className="w-10 h-10 text-[#E07A5F]" />
                  <div className="flex items-center gap-0.5 text-[#E2C044]">
                    {[...Array(testimonials[tIdx].rating || 5)].map((_, j) => (
                      <Star key={j} className="w-4 h-4 fill-current" />
                    ))}
                  </div>
                </div>
                <p className="font-display text-2xl md:text-3xl text-[#2D2623] leading-snug">
                  {testimonials[tIdx].quote}
                </p>
                <div className="mt-10 pt-7 border-t border-[#A39171]/20 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                  <div>
                    <p className="font-medium text-[#2D2623] text-lg">{testimonials[tIdx].name}</p>
                    <p className="text-sm text-[#5C524E] mt-0.5">
                      {testimonials[tIdx].role} · {testimonials[tIdx].location}
                    </p>
                  </div>
                  <p className="text-xs uppercase tracking-[0.2em] text-[#A39171]">
                    {String(tIdx + 1).padStart(2, "0")} / {String(testimonials.length).padStart(2, "0")}
                  </p>
                </div>
              </article>

              {/* dots */}
              <div className="flex items-center justify-center gap-2 mt-8" data-testid="testimonial-dots">
                {testimonials.map((t, i) => (
                  <button
                    key={t.id}
                    onClick={() => setTIdx(i)}
                    aria-label={`Go to testimonial ${i + 1}`}
                    data-testid={`testimonial-dot-${i}`}
                    className={`h-1.5 rounded-full transition-all duration-500 ${
                      i === tIdx ? "w-10 bg-[#E07A5F]" : "w-2 bg-[#A39171]/30 hover:bg-[#A39171]/60"
                    }`}
                  />
                ))}
              </div>
            </div>
          )}
        </div>
      </section>

      {/* NEUROMODULATION HIGHLIGHT */}
      <section className="bg-[#2D2623] text-[#FDFBF7] py-24 md:py-32 relative overflow-hidden">
        <div className="absolute inset-0 grain opacity-30 pointer-events-none" />
        <div className="max-w-7xl mx-auto px-6 md:px-10 grid grid-cols-1 md:grid-cols-12 gap-12 items-center relative">
          <div className="md:col-span-6 order-2 md:order-1 reveal">
            <p className="eyebrow text-[#E2C044] mb-5">{t("Interventional Neuromodulation")}</p>
            <h2 className="font-display text-4xl md:text-5xl tracking-tight leading-tight">
              {t("When medication isn't enough,")}<br />
              <span className="italic text-[#E07A5F]">{t("science finds another way.")}</span>
            </h2>
            <p className="mt-6 text-lg text-[#FDFBF7]/70 leading-relaxed max-w-lg">
              {t("Our neuromodulation suite is equipped with the latest rTMS technology — a non-invasive, FDA-approved therapy for treatment-resistant depression, OCD, anxiety and more.")}
            </p>
            <Link
              to="/neuromodulation"
              data-testid="neuro-cta"
              className="mt-10 inline-flex items-center gap-2 px-7 h-12 rounded-full bg-[#E07A5F] hover:bg-[#FDFBF7] hover:text-[#2D2623] transition-all font-medium"
            >
              {t("Explore the Programme")} <ArrowUpRight className="w-4 h-4" />
            </Link>
          </div>
          <div className="md:col-span-6 order-1 md:order-2 reveal">
            <div className="rounded-3xl overflow-hidden aspect-square relative">
              <img src={NEURO_IMG} alt="Neuromodulation technology" className="w-full h-full object-cover" />
            </div>
          </div>
        </div>
      </section>

      {/* SEXOLOGY HIGHLIGHT */}
      <section className="py-24 md:py-32 bg-[#F4EFE6]/60">
        <div className="max-w-7xl mx-auto px-6 md:px-10 grid grid-cols-1 md:grid-cols-12 gap-12 items-center">
          <div className="md:col-span-6 reveal">
            <div className="rounded-3xl overflow-hidden aspect-square relative">
              <img src={SEXOLOGY_IMG} alt="Confidential sexology consultation" className="w-full h-full object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-[#2D2623]/45 via-transparent to-transparent" />
              <div className="absolute bottom-6 left-6 right-6 bg-[#FDFBF7]/95 backdrop-blur-md rounded-2xl p-5 border border-white/40 flex items-center gap-3">
                <Lock className="w-4 h-4 text-[#E07A5F] shrink-0" />
                <p className="text-sm text-[#2D2623] font-medium leading-tight">
                  {t("Private cabin · dedicated slot · records never shared")}
                </p>
              </div>
            </div>
          </div>
          <div className="md:col-span-6 reveal">
            <p className="eyebrow text-[#A39171] mb-5">{t("Sexology & Psychosexual Medicine")}</p>
            <h2 className="font-display text-4xl md:text-5xl text-[#2D2623] tracking-tight leading-tight">
              {t("The hardest part is")}<br />
              <span className="italic text-[#E07A5F]">{t("saying it out loud.")}</span>
            </h2>
            <p className="mt-6 text-lg text-[#5C524E] leading-relaxed max-w-lg">
              {t("Erectile dysfunction, premature ejaculation, low desire, vaginismus, unconsummated marriage, Dhat syndrome — treated as medicine, never as taboo. For men, women and couples.")}
            </p>
            <ul className="mt-7 space-y-3 text-[#2D2623]">
              {[
                "30+ psychosexual conditions treated",
                "Myth-busting education before any prescription",
                "Ask us anonymously before you ever visit",
              ].map((p) => (
                <li key={p} className="flex items-start gap-3">
                  <span className="w-1.5 h-1.5 rounded-full bg-[#E07A5F] mt-2.5" />
                  <span>{t(p)}</span>
                </li>
              ))}
            </ul>
            <Link
              to="/sexology"
              data-testid="sexology-cta"
              className="mt-10 inline-flex items-center gap-2 px-7 h-12 rounded-full bg-[#2D2623] hover:bg-[#E07A5F] text-[#FDFBF7] transition-all font-medium"
            >
              {t("Explore the Clinic")} <ArrowUpRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* BLOG PREVIEW */}
      <section className="py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-12 reveal">
            <div className="max-w-xl">
              <p className="eyebrow text-[#A39171] mb-4">{t("From our Journal")}</p>
              <h2 className="font-display text-4xl md:text-5xl text-[#2D2623] tracking-tight">
                {t("Notes from the practice.")}
              </h2>
            </div>
            <Link to="/blog" data-testid="blog-cta" className="inline-flex items-center gap-2 text-[#2D2623] hover:text-[#E07A5F] font-medium link-underline">
              {t("All articles")} <ArrowUpRight className="w-4 h-4" />
            </Link>
          </div>

          <div
            onMouseEnter={() => setPostsPaused(true)}
            onMouseLeave={() => setPostsPaused(false)}
            data-testid="journal-carousel"
          >
            <div
              key={postOffset}
              className="grid grid-cols-1 md:grid-cols-3 gap-6 fade-up"
            >
              {visiblePosts.map((p, i) => (
                <Link
                  key={`${postOffset}-${p.id}`}
                  to={`/blog/${p.slug}`}
                  data-testid={`home-blog-${i}`}
                  style={{ animationDelay: `${i * 80}ms` }}
                  className="fade-up group bg-[#F4EFE6] rounded-3xl overflow-hidden border border-[#A39171]/10 hover:shadow-[0_24px_60px_rgba(163,145,113,0.15)] transition-all duration-500 hover:-translate-y-1"
                >
                  <div className="aspect-[4/3] overflow-hidden">
                    <img src={p.cover_image || DEADD_IMG} alt={p.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                  </div>
                  <div className="p-7">
                    <p className="eyebrow text-[#A39171] mb-3">{p.category}</p>
                    <h3 className="font-display text-2xl text-[#2D2623] leading-tight tracking-tight">{p.title}</h3>
                    <p className="text-[#5C524E] mt-3 line-clamp-2">{p.excerpt}</p>
                  </div>
                </Link>
              ))}
            </div>

            {postGroupCount > 1 && (
              <div className="flex items-center justify-center gap-2 mt-10" data-testid="journal-dots">
                {Array.from({ length: postGroupCount }).map((_, i) => (
                  <button
                    key={i}
                    onClick={() => setPostOffset(i * 3)}
                    aria-label={`Go to article group ${i + 1}`}
                    data-testid={`journal-dot-${i}`}
                    className={`h-1.5 rounded-full transition-all duration-500 ${
                      i === currentGroup ? "w-10 bg-[#E07A5F]" : "w-2 bg-[#A39171]/30 hover:bg-[#A39171]/60"
                    }`}
                  />
                ))}
              </div>
            )}
          </div>
        </div>
      </section>

      {/* VISIT / CTA */}
      <section className="pb-24 md:pb-32">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <div className="bg-[#F4EFE6] rounded-[32px] p-10 md:p-16 grid grid-cols-1 md:grid-cols-12 gap-10 items-center relative overflow-hidden">
            <div className="absolute -top-20 -right-20 w-60 h-60 rounded-full bg-[#E2C044]/30 blur-3xl" />
            <div className="md:col-span-7 relative">
              <p className="eyebrow text-[#A39171] mb-4">{t("Visit Manas")}</p>
              <h2 className="font-display text-4xl md:text-5xl text-[#2D2623] leading-tight tracking-tight">
                {t("Begin the conversation,")}<br /><span className="italic text-[#E07A5F]">{t("at your pace.")}</span>
              </h2>
              <p className="mt-5 text-lg text-[#5C524E] max-w-lg">
                {t("We see each appointment as the start of a relationship. Walk in, call, or request a consultation online — whichever feels right.")}
              </p>
              <div className="mt-8 flex flex-col sm:flex-row gap-4">
                <Button
                  data-testid="cta-book-btn"
                  onClick={() => setBookOpen(true)}
                  className="rounded-full bg-[#2D2623] hover:bg-[#E07A5F] text-[#FDFBF7] px-8 h-12 font-medium"
                >
                  {t("Book Online")}
                </Button>
                <a
                  href="tel:+919427560101"
                  data-testid="cta-call-btn"
                  className="rounded-full border border-[#2D2623] text-[#2D2623] hover:bg-[#2D2623] hover:text-[#FDFBF7] px-8 h-12 inline-flex items-center justify-center font-medium transition-colors"
                >
                  {t("Call")} +91 94275 60101
                </a>
              </div>
            </div>
            <div className="md:col-span-5 relative">
              <div className="bg-[#FDFBF7] rounded-3xl border border-[#A39171]/15 p-7">
                <p className="text-sm text-[#A39171] uppercase tracking-[0.15em]">{t("Visit Hours")}</p>
                <div className="mt-5 space-y-4">
                  <div className="flex items-start gap-3">
                    <Clock className="w-5 h-5 text-[#E07A5F] mt-0.5" />
                    <div>
                      <p className="text-[#2D2623] font-medium">{t("Mon – Sat")}</p>
                      <p className="text-[#5C524E] text-sm">09:00 AM – 08:00 PM</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <Clock className="w-5 h-5 text-[#E07A5F] mt-0.5" />
                    <div>
                      <p className="text-[#2D2623] font-medium">{t("For Emergency")}</p>
                      <p className="text-[#5C524E] text-sm">{t("24 × 7 Open")}</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <MapPin className="w-5 h-5 text-[#E07A5F] mt-0.5" />
                    <p className="text-[#5C524E] text-sm leading-relaxed">
                      2nd floor, Carlton Square, Near Madhavdeep Complex,<br />Kalanala, Bhavnagar — 364001
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <InstagramStrip limit={3} />

      <AppointmentDialog open={bookOpen} onOpenChange={setBookOpen} />
    </div>
  );
}
